// /frontend/ChatWindow.js (v2.7.7 재시도 토큰 갱신 로직 강화 업데이트)
// 역할: 사용자와의 채팅 UI 및 서버와의 모든 통신을 담당하는 핵심 컴포넌트입니다.
// v2.7.7 업데이트 핵심:
// - [재시도 로직 강화] 사용자가 '재시도' 버튼을 클릭할 때, 과거의 요청 데이터를 그대로 보내는 대신 '토큰(Token)'만 현재 시점의 최신 값으로 교체하여 전송하도록 수정했습니다.
//   - 이를 통해 과거 토큰 사용으로 인한 '409 Conflict' 또는 '무한 대기' 현상을 클라이언트 단에서 원천적으로 방지합니다.
//   - 서버 입장에서는 유효한 토큰을 가진 '정당한 요청'으로 인식되므로, 중단된 작업을 즉시 재개하거나 결과를 반환받을 수 있습니다.
// v2.7.6 업데이트 핵심:
// - [치명적 버그 수정] Polling 로직과 토큰 갱신 로직 간의 경합 조건(Race Condition) 해결.
//   - pollingTimerRef를 도입하여 중복 폴링 방지 및 sendMessage 시 타이머 강제 초기화 적용.

import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AppContext } from './contexts/AppContext';
import TokenManager from './TokenManager';

// Helper: Toast Notification Component
const Toast = ({ message }) => {
    if (!message) return null;
    return <div className="toast-notification">{message}</div>;
};

// Helper: Fetches with retry and timeout logic
async function fetchWithRetry(url, options, timeout = 20000) {
    try {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(id);
        return response;
    } catch (error) {
        console.warn("Fetch failed or timed out. Handing over to polling logic.", error.name);
        return null;
    }
}

const CodeBlock = React.memo(({ className, children }) => {
    const [isCopied, setIsCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(String(children));
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    }, [children]);

    return (
        <div className="code-block-wrapper">
            <div className="code-block-header">
                <span>{language}</span>
                <button onClick={handleCopy}>{isCopied ? '✓ 복사됨' : '복사'}</button>
            </div>
            <SyntaxHighlighter children={String(children).replace(/\n$/, '')} style={vscDarkPlus} language={language} PreTag="div" />
        </div>
    );
});

const MessageContent = React.memo(({ content }) => {
    return (
        <ReactMarkdown
            children={content}
            remarkPlugins={[remarkGfm]}
            components={{
                code: ({ node, inline, className, children, ...props }) =>
                    !inline ? <CodeBlock className={className}>{children}</CodeBlock> : <code className={className} {...props}>{children}</code>,
            }}
        />
    );
});

const MessageActions = ({ text }) => {
    const [isCopied, setIsCopied] = useState(false);
    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    }, [text]);

    return (
        <div className="message-actions">
            <button onClick={handleCopy} className={`action-icon-button ${isCopied ? 'copied' : ''}`} title="복사">
                {isCopied ? '✓' : '📋'}
            </button>
        </div>
    );
};


function ChatWindow({ userId, userData, initialMessages, onLogout, onUserDataUpdate }) {
    const [messages, setMessages] = useState(initialMessages || []);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [thinkingMessage, setThinkingMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [lastUserMessageId, setLastUserMessageId] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [typingQueue, setTypingQueue] = useState([]);
    const typingIntervalRef = useRef(null);
    
    const [isPollingFailed, setIsPollingFailed] = useState(false);
    const [lastRequestBeforePolling, setLastRequestBeforePolling] = useState(null);
    
    // --- [v2.7.6 버그 수정] ---
    // 폴링 타이머를 관리하기 위한 ref를 추가합니다. 이를 통해 여러 개의 폴링 타이머가 동시에 실행되는 것을 방지합니다.
    const pollingTimerRef = useRef(null);
    const pollingCountRef = useRef(0);
    const POLLING_TIMEOUT_LIMIT = 40; // 1.5초 간격으로 40회 = 약 1분

    const chatWindowRef = useRef(null);
    const textareaRef = useRef(null);
    const lastScrollTop = useRef(0);
    const { theme, toggleTheme, fontSize, handleFontSizeChange } = useContext(AppContext);
    const API_URL = process.env.REACT_APP_API_URL || '';

    const isMobile = useMemo(() => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);

    const scrollToBottom = (behavior = 'auto') => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTo({ top: chatWindowRef.current.scrollHeight, behavior });
        }
    };
    
    useEffect(() => {
        scrollToBottom('auto');
    }, []);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [inputValue]);

    useEffect(() => {
        if (!isLoading && textareaRef.current && !isPollingFailed) {
            const focusTimeout = isMobile ? 100 : 0;
            setTimeout(() => textareaRef.current.focus(), focusTimeout);
        }
    }, [isLoading, isMobile, isPollingFailed]);
    
    useEffect(() => {
        if (!chatWindowRef.current || messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];
        if (!lastMessage || lastMessage.sender !== 'ai' || !lastMessage.id) return;

        const lastAiMessageElement = document.getElementById(lastMessage.id);
        const inputAreaContainer = document.querySelector('.input-area-container');
        if (!lastAiMessageElement || !inputAreaContainer) return;

        const chatRect = chatWindowRef.current.getBoundingClientRect();
        const lastAiMessageRect = lastAiMessageElement.getBoundingClientRect();
        
        const scrollTriggerLine = chatRect.bottom - inputAreaContainer.offsetHeight - 20;

        if (lastAiMessageRect.bottom > scrollTriggerLine) {
            const userMessageElement = document.getElementById(lastUserMessageId);
            if (userMessageElement) {
                userMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                scrollToBottom('smooth');
            }
        }
        
    }, [messages, lastUserMessageId]);


    // [v2.7.2 로직 보존] 타이핑 효과를 처리하는 useEffect
    useEffect(() => {
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }

        if (typingQueue.length > 0 && isLoading) {
            typingIntervalRef.current = setInterval(() => {
                setTypingQueue(prevQueue => {
                    const newQueue = [...prevQueue];
                    const nextWord = newQueue.shift();

                    if (nextWord) {
                        setThinkingMessage(prev => prev + nextWord + ' ');
                    }

                    if (newQueue.length === 0) {
                        clearInterval(typingIntervalRef.current);
                        typingIntervalRef.current = null;
                    }
                    
                    return newQueue;
                });
            }, 50);
        }

        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
        };
    }, [typingQueue, isLoading]);


    const handleApiResponse = async (requestData, isPolling = false, isRetry = false) => {
        if (isPolling) {
            pollingCountRef.current += 1;
            if (pollingCountRef.current > POLLING_TIMEOUT_LIMIT) {
                console.error("Polling timed out after 1 minute.");
                
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                }
                setTypingQueue([]); 
                setThinkingMessage('');

                setThinkingMessage("응답이 지연되고 있습니다. 잠시 후 재시도 해주세요.");
                setIsPollingFailed(true);
                setIsLoading(false);
                return;
            }
        }

        try {
            let bodyData;
            if (isPolling) {
                bodyData = { userId: requestData.userId, token: TokenManager.getToken() };
            } else {
                bodyData = requestData;
            }

            const response = await fetchWithRetry(`${API_URL}/api/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });
    
            if (response === null) {
                if (!isPolling) console.log("Initial request timed out. Assuming backend is busy, starting poll.");
                
                // --- [v2.7.6 수정] 폴링 타이머 중복 방지 ---
                if (!pollingTimerRef.current) {
                    pollingTimerRef.current = setTimeout(() => {
                        pollingTimerRef.current = null;
                        handleApiResponse({ userId: requestData.userId }, true, false);
                    }, 1500);
                }
                return;
            }
    
            if (response.status === 409) {
                if (isRetry) throw new Error('Token mismatch auto-retry also failed.');
                console.warn('Token mismatch detected! Auto-syncing and retrying...');
                const errorData = await response.json();
                if (errorData.activeRequestToken) TokenManager.setToken(errorData.activeRequestToken);
                await handleApiResponse(bodyData, isPolling, true); 
                return;
            }
            
            if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
            
            const data = await response.json();
    
            if (data.activeRequestToken) {
                TokenManager.setToken(data.activeRequestToken);
            }
    
            if (data.status === 'processing') {
                if (data.step_message) {
                    const words = data.step_message.trim().split(/\s+/);
                    if (words.length > 0 && words[0] !== '') {
                        setTypingQueue(prevQueue => [...prevQueue, ...words]);
                    }
                }
                
                // --- [v2.7.6 수정] 폴링 타이머 중복 방지 ---
                // 이미 다른 폴링 타이머가 실행 중인 경우, 새로운 타이머를 시작하지 않습니다.
                // 이를 통해 여러 폴링 요청이 동시에 서버로 전송되는 것을 방지합니다.
                if (!pollingTimerRef.current) {
                    console.log(`[v2.7.6] Backend is processing... Starting new poll #${pollingCountRef.current}`);
                    pollingTimerRef.current = setTimeout(() => {
                        pollingTimerRef.current = null; // 타이머 실행 후에는 반드시 초기화하여 다음 폴링이 가능하도록 합니다.
                        handleApiResponse({ userId: requestData.userId }, true, false);
                    }, 1500);
                } else {
                    console.log(`[v2.7.6] Polling already in progress. Skipping new timer.`);
                }

            } else {
                // --- [v2.7.6 수정] 정상 응답 수신 시 모든 폴링 관련 상태 초기화 ---
                if (pollingTimerRef.current) {
                    clearTimeout(pollingTimerRef.current);
                    pollingTimerRef.current = null;
                }
                pollingCountRef.current = 0;
                
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                }
                setTypingQueue([]);
                setThinkingMessage('');

                if (data.replies && data.replies.length > 0) {
                    const aiMessages = data.replies.map(reply => ({ ...reply, id: `ai-${Date.now()}-${Math.random()}` }));
                    setMessages(prev => [...prev, ...aiMessages]);
                }
                setIsLoading(false);
            }
        } catch (error) {
            console.error("[handleApiResponse] Critical Error:", error);
            const errorMessage = { id: `ai-${Date.now()}`, text: '오류가 발생했습니다. 메시지 전송에 실패했습니다.', sender: 'ai' };
            setMessages(prev => [...prev, errorMessage]);
            
            // --- [v2.7.6 수정] 오류 발생 시에도 모든 폴링 관련 상태 초기화 ---
            if (pollingTimerRef.current) {
                clearTimeout(pollingTimerRef.current);
                pollingTimerRef.current = null;
            }
            pollingCountRef.current = 0;
            
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
            }
            setTypingQueue([]);
            setThinkingMessage('');
            setIsLoading(false);
        }
    };

    const sendMessage = async (messageText) => {
        const text = messageText.trim();
        if (!text || isLoading) return;

        // --- [v2.7.6 수정] ---
        // 사용자가 새로운 메시지를 보낼 때가 모든 비동기 상태를 초기화하는 유일한 시점이어야 합니다.
        // 이를 통해 이전 요청의 폴링 상태가 다음 요청에 영향을 미치는 것을 완벽하게 차단합니다.
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }
        setTypingQueue([]);
        setThinkingMessage('');
        pollingCountRef.current = 0;
        setIsPollingFailed(false); // 재시도 상태도 여기서 초기화합니다.

        setIsLoading(true);
        const userMessage = { text, sender: 'user', id: `user-${Date.now()}` };
        setLastUserMessageId(userMessage.id);
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setTimeout(() => scrollToBottom('smooth'), 0);

        const requestData = {
            userId,
            message: { text, sender: 'user' },
            token: TokenManager.getToken(),
        };
        
        setLastRequestBeforePolling(requestData);

        await handleApiResponse(requestData, false);
    };

    const handleRetry = async () => {
        if (!lastRequestBeforePolling) {
            console.error("No request data to retry.");
            setThinkingMessage("재시도할 요청 정보가 없습니다. 새로고침 해주세요.");
            return;
        }

        // --- [v2.7.6 수정] 재시도 시에도 모든 폴링 상태를 깨끗하게 초기화합니다. ---
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        setIsPollingFailed(false);
        pollingCountRef.current = 0;
        setThinkingMessage('');
        setTypingQueue([]);
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }

        setIsLoading(true);
        
        // --- [v2.7.7 핵심 업데이트] 재시도 토큰 갱신 로직 ---
        // 저장된 과거 요청 객체에서 '토큰'만 현재 시점의 최신 토큰으로 교체합니다.
        // 이렇게 하면 내용은 같지만, 서버가 인정하는 유효한 '새 요청'이 되어 409 충돌을 피하고 작업을 강제 재개할 수 있습니다.
        const retryRequestData = {
            ...lastRequestBeforePolling,
            token: TokenManager.getToken() // 최신 토큰 주입
        };

        console.log("Retrying request with FRESH token...", retryRequestData);
        
        // isPolling = false로 설정하여, 단순 상태 확인이 아니라 '정식 API 요청'을 다시 보냅니다.
        // isRetry = true로 설정하여 내부 로직에서 재시도임을 인지하게 합니다.
        await handleApiResponse(retryRequestData, false, true); 
    };


    const handleEnterPress = (event) => {
        if (isMobile) {
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            sendMessage(inputValue);
        }
    };
    
    const showToast = (message) => {
        setToastMessage(message);
        setTimeout(() => setToastMessage(''), 2500);
    };

    const handleCopyId = () => {
        navigator.clipboard.writeText(userId);
        showToast('ID가 복사되었습니다.');
    };

    const filteredMessages = useMemo(() => {
        if (!searchQuery) return messages;
        return messages.filter(msg => (msg.text || '').toLowerCase().includes(searchQuery.toLowerCase()));
    }, [messages, searchQuery]);

    const handleScroll = () => {
        if (chatWindowRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
            setShowScrollButton(scrollHeight - scrollTop > clientHeight * 1.5);
            if (scrollTop > lastScrollTop.current && scrollTop > 50) {
                setIsHeaderVisible(false);
            } else {
                setIsHeaderVisible(true);
            }
            lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
        }
    };

    return (
        <>
            <Toast message={toastMessage} />
            {isSearchOpen && (
               <div className="search-modal-overlay" onClick={() => setIsSearchOpen(false)}>
                   <div className="search-modal-content" onClick={e => e.stopPropagation()}>
                       <h3>대화 내용 검색</h3>
                       <input
                           type="text" placeholder="검색어를 입력하세요..."
                           value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus
                       />
                   </div>
               </div>
            )}
            {isSettingsOpen && (
                <div className="search-modal-overlay" onClick={() => setIsSettingsOpen(false)}>
                    <div className="search-modal-content" onClick={e => e.stopPropagation()}>
                        <h3>설정</h3>
                        <div className="settings-id-display">
                            <p>회원님의 고유 ID입니다.</p>
                            <div className="id-container">
                                <span>{userId}</span>
                                <button onClick={handleCopyId} className="id-copy-button" title="ID 복사">📋</button>
                            </div>
                        </div>
                        <button className="new-user-button logout-button" onClick={onLogout}>로그아웃</button>
                    </div>
                </div>
            )}

            <div className="chat-window" style={{ fontSize: `${fontSize}px` }}>
                <div className={`chat-header ${isHeaderVisible ? 'visible' : ''}`}>
                    <div className="chat-header-left">
                        <div className="chat-header__title">
                            {userData?.ai_name ? `EGO (${userData.ai_name}) 시스템` : 'EGO Lite 1.0v'}
                        </div>
                    </div>
                    <div className="chat-header-right">
                        <button onClick={() => handleFontSizeChange('decrease')} className="header-button" title="글자 작게">-</button>
                        <button onClick={() => handleFontSizeChange('increase')} className="header-button" title="글자 크게">+</button>
                        <button onClick={() => setIsSearchOpen(true)} className="header-button" title="검색">🔍</button>
                        <button onClick={toggleTheme} className="header-button" title="테마 변경">{theme === 'light' ? '🌙' : '☀️'}</button>
                        <button onClick={() => setIsSettingsOpen(true)} className="header-button" title="설정">⚙️</button>
                    </div>
                </div>

                <div className="messages" ref={chatWindowRef} onScroll={handleScroll}>
                    {filteredMessages.map((message) => (
                        <div id={message.id} key={message.id || `msg-${Math.random()}`} className={`message-container ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}>
                            <div className={`message-bubble ${message.text && message.text.length > 80 ? 'wide' : ''}`}>
                                <MessageContent content={message.text || ''} />
                            </div>
                            {message.text && <MessageActions text={message.text} />}
                        </div>
                    ))}
                    {(isLoading || isPollingFailed) && (
                        <div className="message-container ai-message">
                            <div className="message-bubble">
                                <div className="typing-indicator">
                                    {thinkingMessage || '딥러닝 분석중'}<span className="dots"></span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <button className={`scroll-to-bottom ${showScrollButton ? 'visible' : ''}`} onClick={() => scrollToBottom('smooth')} title="맨 아래로">↓</button>

                <div className="input-area-container">
                    {isPollingFailed ? (
                        <div className="input-area retry-area">
                            <textarea
                                ref={textareaRef}
                                placeholder="응답 지연 발생..."
                                disabled={true}
                                rows={1}
                            />
                            <button onClick={handleRetry} className="retry-button" title="재시도">↻</button>
                        </div>
                    ) : (
                        <div className="input-area">
                            <textarea
                                ref={textareaRef}
                                placeholder="메시지를 입력하세요..."
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={handleEnterPress}
                                disabled={isLoading}
                                rows={1}
                                autoFocus
                            />
                            <button onClick={() => sendMessage(inputValue)} disabled={!inputValue.trim() || isLoading}>➤</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default ChatWindow;
